import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import './padron-mapas-list.css';
import CardBasic from '../../components/card/card-basic.jsx';
import CardContainer from '../../components/card/card-container.jsx';
import { Panel } from '../../components/panel/panel.jsx';
import LabelCategory from '../../components/label/label-category.jsx';
import Api from '../../api/global.service';
import http from '../../api/http-common.jsx';
import Swal from 'sweetalert2';
import BlockUi from 'react-block-ui';
import env from '../../environment.js';
import { getRiesgoProductor } from '../../constants/riesgoProductor.js';

import MapSideTable from '../../components/tables/map-side-table.jsx';
import SelectCiclo from '../../components/select/select-ciclo.jsx';
import SelectRiesgo from '../../components/select/select-riesgo.jsx';

import GoogleMapReact from 'google-map-react';
import NorthIcon from '../../components/map/north-icon.jsx';
import MapRecenterButton from '../../components/map/map-recenter-button.jsx';
import descargaKML from '../../utils/downloadKML.js';
import useGeoPuntosLayer from '../../hooks/useGeoPuntosLayer.jsx';

import useEtiquetas from '../../hooks/useEtiquetas';
import transformWord from '../../utils/transformWord';
import SimbologiaLegend from '../../components/map/simbologia-legend.jsx';
import MapTypeToggle from '../../components/map/map-type-toggle.jsx';

const Breadcrumb = ({ items }) => (
  <div className="mb-2 d-flex align-items-center" style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4 }}>
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <i className="fa fa-chevron-right mx-2" aria-hidden="true" style={{ fontSize: 9, color: env.colors.neutral_300 }} />}
        {!item.active ? (
          <a
            onClick={item.onClick}
            style={{
              color: env.colors.neutral_900,
              textDecoration: 'underline',
              cursor: item.onClick ? 'pointer' : 'default',
              textTransform: 'uppercase',
            }}>
            {item.label}
          </a>
        ) : (
          <span style={{ color: env.colors.neutral_900, textTransform: 'uppercase' }}>{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const RIESGO_COLORS = { 1: '#27AE60', 2: '#F2C94C', 3: env.colors.error };
const getRiesgoColor = idRiesgo => RIESGO_COLORS[idRiesgo] || env.colors.neutral_300;

export const CircleMarker = ({ number, color, onClick, title, size = 32 }) => (
  <div onClick={onClick} title={title} style={{ transform: 'translate(-50%, -50%)', cursor: onClick ? 'pointer' : 'default' }}>
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color || env.colors.neutral_600,
        border: '2px solid #fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: 11,
      }}>
      {number}
    </div>
  </div>
);

export const PillMarker = ({ label, color, onClick, title }) => (
  <div onClick={onClick} title={title} style={{ transform: 'translate(-50%, -50%)', cursor: onClick ? 'pointer' : 'default' }}>
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 10px',
        borderRadius: 14,
        backgroundColor: '#fff',
        border: `1.5px solid ${env.colors.neutral_900}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
        color: env.colors.neutral_900,
        fontWeight: 700,
        fontSize: 11,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}>
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: color || env.colors.primary_500,
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
    </div>
  </div>
);

const MapLegendRow = ({ items }) => {
  if (!items.length) return null;
  return (
    <div style={{ paddingTop: 20 }}>
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 20, fontSize: 13, color: env.colors.neutral_700 }}>
        {items.map((it, i) => (
          <span key={i} className="d-flex align-items-center" style={{ gap: 6, lineHeight: 1 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: it.color,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span>
              {it.label} · {it.count}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

// El 0 exacto se trata como "sin coordenada": varias localidades del catálogo se dieron de alta con latitud/longitud 0.0 en lugar de
// null, y un null convertido a número también da 0. Ninguna zona donde opera la app está sobre el ecuador ni el meridiano de
// Greenwich, así que (0, 0) solo puede ser un dato faltante.
export const isValidLat = value =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  Number.isFinite(Number(value)) &&
  Math.abs(Number(value)) <= 90 &&
  Number(value) !== 0;

export const isValidLng = value =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  Number.isFinite(Number(value)) &&
  Math.abs(Number(value)) <= 180 &&
  Number(value) !== 0;

export const DEFAULT_MAP_CENTER = { lat: 17.040386, lng: -91.842369 };

export const safeCenter = center => (center && isValidLat(center.lat) && isValidLng(center.lng) ? center : DEFAULT_MAP_CENTER);

export const coordsCenter = coords => {
  const lat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
  const lng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
  return { lat, lng };
};

const parsePoligonoPrimerPunto = poligono => {
  if (!poligono) return null;
  try {
    const [lng, lat] = JSON.parse(poligono).coordinates[0][0];
    return isValidLat(lat) && isValidLng(lng) ? { lat: Number(lat), lng: Number(lng) } : null;
  } catch (e) {
    return null;
  }
};

export const getParcelaPosition = data => {
  const fromPoligono = parsePoligonoPrimerPunto(data.poligono);
  if (fromPoligono) return fromPoligono;
  if (isValidLat(data.centroide_lat) && isValidLng(data.centroide_lng)) {
    return { lat: Number(data.centroide_lat), lng: Number(data.centroide_lng) };
  }
  return null;
};

const getPoligonoCentroide = poligono => {
  if (!poligono) return null;
  try {
    const ring = JSON.parse(poligono).coordinates[0];
    const coords = ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) })).filter(c => isValidLat(c.lat) && isValidLng(c.lng));
    return coords.length ? coordsCenter(coords) : null;
  } catch (e) {
    return null;
  }
};

export const getParcelaCentro = data => getPoligonoCentroide(data.poligono) || getParcelaPosition(data);

export const getParcelaVertices = data => {
  if (data.poligono) {
    try {
      const ring = JSON.parse(data.poligono).coordinates[0];
      const vertices = ring
        .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
        .filter(c => isValidLat(c.lat) && isValidLng(c.lng));
      if (vertices.length) return vertices;
    } catch (e) {
      // cae a la posición puntual
    }
  }
  const pos = getParcelaPosition(data);
  return pos ? [pos] : [];
};

export const fitMapToPositions = (map, maps, positions, { padding = 60, maxZoom = 17 } = {}) => {
  const valid = (positions || []).filter(Boolean);
  if (!map || !maps || !valid.length) return;
  const bounds = new maps.LatLngBounds();
  valid.forEach(pos => bounds.extend(pos));
  map.fitBounds(bounds, padding);
  // Con un solo punto (o todos juntos) fitBounds acerca al máximo; se limita el acercamiento.
  maps.event.addListenerOnce(map, 'idle', () => {
    if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
  });
};

export const fitBoundsToParcelas = (map, maps, parcelas) => {
  if (!map || !maps || !parcelas?.length) return;
  const bounds = new maps.LatLngBounds();
  let hasPoint = false;
  parcelas.forEach(p => {
    const pos = getParcelaPosition(p);
    if (pos) {
      bounds.extend(pos);
      hasPoint = true;
    }
  });
  if (hasPoint) map.fitBounds(bounds, 60);
};

const PadronMapasList = () => {
  const history = useHistory();
  const location = useLocation();
  const [idCiclo, setIdCiclo] = useState(0);
  const { etiquetas } = useEtiquetas();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlIdLocalidad = urlParams.get('localidad');
  const urlProductorUuid = urlParams.get('productor');

  const incomingMapStateRef = useRef(
    location.state?.center && location.state?.zoom != null ? { center: location.state.center, zoom: location.state.zoom } : null
  );

  const goToMapasUrl = params => {
    const search = new URLSearchParams();
    if (params.idLocalidad) search.set('localidad', params.idLocalidad);
    if (params.productorUuid) search.set('productor', params.productorUuid);
    const query = search.toString();
    const newSearch = query ? `?${query}` : '';
    if (newSearch === location.search) return;
    history.push({ pathname: '/admin/padron/mapas/list', search: newSearch });
  };

  // ---------------- Nivel 1: Comunidades ----------------
  const [dataLocalidades, setDataLocalidades] = useState([]);
  const [riesgosPorLocalidad, setRiesgosPorLocalidad] = useState({});
  const [blockingLocalidades, setBlockingLocalidades] = useState(true);
  const [zoomLocalidades, setZoomLocalidades] = useState(9);
  const [centerLocalidades, setCenterLocalidades] = useState(DEFAULT_MAP_CENTER);
  const [mapTypeNivel1, setMapTypeNivel1] = useState('hybrid');
  const [searchNivel1, setSearchNivel1] = useState('');
  const [highlightedLocalidadId, setHighlightedLocalidadId] = useState(null);
  const mapSideTableNivel1Ref = useRef(null);
  const [mapLocalidades, setMapLocalidades] = useState(null);
  const [mapsLocalidades, setMapsLocalidades] = useState(null);
  const [geoPuntosCooperativa, setGeoPuntosCooperativa] = useState([]);
  const [showSimbologiaNivel1, setShowSimbologiaNivel1] = useState(true);
  const geoPuntosCooperativaForLayer = useMemo(() => (showSimbologiaNivel1 ? geoPuntosCooperativa : []), [
    showSimbologiaNivel1,
    geoPuntosCooperativa,
  ]);
  const { legendData: legendDataCooperativa } = useGeoPuntosLayer(mapLocalidades, mapsLocalidades, geoPuntosCooperativaForLayer);

  const [localidad, setLocalidad] = useState(null);

  // ---------------- Nivel 2: Parcelas/productores de la comunidad ----------------
  const [dataProductoresLocalidad, setDataProductoresLocalidad] = useState([]);
  const [blockingProductores, setBlockingProductores] = useState(true);
  const [mapProductores, setMapProductores] = useState(null);
  const [mapsProductores, setMapsProductores] = useState(null);
  const [zoomProductores, setZoomProductores] = useState(9);
  const [centerProductores, setCenterProductores] = useState(DEFAULT_MAP_CENTER);
  const [mapTypeNivel2, setMapTypeNivel2] = useState('hybrid');
  const [searchNivel2, setSearchNivel2] = useState('');
  const [highlightedProductorUuid, setHighlightedProductorUuid] = useState(null);
  const mapSideTableNivel2Ref = useRef(null);
  const [geoPuntosLocalidad, setGeoPuntosLocalidad] = useState([]);
  const [showSimbologiaNivel2, setShowSimbologiaNivel2] = useState(true);
  const nivel2Anchor = useMemo(() => {
    if (isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud)) {
      return { lat: Number(localidad.localidad_latitud), lng: Number(localidad.localidad_longitud) };
    }
    return centerProductores;
  }, [localidad, centerProductores]);
  const geoPuntosLocalidadForLayer = useMemo(() => (showSimbologiaNivel2 ? geoPuntosLocalidad : []), [
    showSimbologiaNivel2,
    geoPuntosLocalidad,
  ]);
  const { legendData: legendDataLocalidad } = useGeoPuntosLayer(mapProductores, mapsProductores, geoPuntosLocalidadForLayer);

  // ---------------- Nivel 3: Unidad de producción de un productor ----------------
  const [productorSeleccionado, setProductorSeleccionado] = useState(null);
  const [mapUnidad, setMapUnidad] = useState(null);
  const [mapsUnidad, setMapsUnidad] = useState(null);
  const [zoomUnidad, setZoomUnidad] = useState(14);
  const [centerUnidad, setCenterUnidad] = useState(DEFAULT_MAP_CENTER);
  const [mapReadyUnidad, setMapReadyUnidad] = useState(false);
  const [mapTypeNivel3, setMapTypeNivel3] = useState('hybrid');
  const [highlightedParcelaId, setHighlightedParcelaId] = useState(null);
  const mapSideTableNivel3Ref = useRef(null);
  const [poligonosUnidad, setPoligonosUnidad] = useState([]);
  const [poligonosUnidadOld, setPoligonosUnidadOld] = useState([]);
  const [geoPuntosUnidad, setGeoPuntosUnidad] = useState([]);
  const [showSimbologiaNivel3, setShowSimbologiaNivel3] = useState(true);
  const nivel3Anchor = useMemo(() => {
    const parcelas = dataProductoresLocalidad.filter(p => p.productor_uuid === productorSeleccionado);
    const validCoords = parcelas.filter(p => isValidLat(p.centroide_lat) && isValidLng(p.centroide_lng));
    if (!validCoords.length) return nivel2Anchor;
    return coordsCenter(validCoords.map(p => ({ lat: Number(p.centroide_lat), lng: Number(p.centroide_lng) })));
  }, [dataProductoresLocalidad, productorSeleccionado, nivel2Anchor]);
  const geoPuntosUnidadForLayer = useMemo(() => (showSimbologiaNivel3 ? geoPuntosUnidad : []), [showSimbologiaNivel3, geoPuntosUnidad]);
  const { legendData: legendDataUnidad } = useGeoPuntosLayer(mapUnidad, mapsUnidad, geoPuntosUnidadForLayer);

  const [savingRiesgo, setSavingRiesgo] = useState(false);

  const [dataProductores, setDataProductores] = useState([]);
  const [topHeight] = useState(520);

  const goToPrintNivel1 = () => {
    const liveCenter = mapLocalidades ? { lat: mapLocalidades.getCenter().lat(), lng: mapLocalidades.getCenter().lng() } : null;
    const liveZoom = mapLocalidades ? mapLocalidades.getZoom() : zoomLocalidades;
    history.push('/admin/padron/mapas/print/cooperativa', {
      center: safeCenter(liveCenter || centerLocalidades),
      zoom: liveZoom,
      dataLocalidades: dataLocalidadesNumeradas,
    });
  };

  const goToPrintNivel2 = () => {
    const liveCenter = mapProductores ? { lat: mapProductores.getCenter().lat(), lng: mapProductores.getCenter().lng() } : null;
    const liveZoom = mapProductores ? mapProductores.getZoom() : zoomProductores;
    history.push(`/admin/padron/mapas/print/localidad/${localidad.localidad_uuid}`, {
      center: safeCenter(liveCenter || nivel2Anchor),
      zoom: liveZoom,
      localidad,
      // Riesgo vigente (el de `localidad` queda con el valor de cuando se abrió).
      riesgoLocalidad: {
        id: riesgoComunidad.id,
        label: riesgosPorLocalidad[localidad.id_localidad]?.cat_localidad_riesgo || null,
        color: riesgoComunidad.id ? riesgoComunidad.color : null,
      },
      dataProductoresLocalidad,
    });
  };

  const goToPrintNivel3 = () => {
    const liveCenter = mapUnidad ? { lat: mapUnidad.getCenter().lat(), lng: mapUnidad.getCenter().lng() } : null;
    const liveZoom = mapUnidad ? mapUnidad.getZoom() : zoomUnidad;
    history.push(`/admin/padron/mapas/print/cuenta/${productorSeleccionado}`, {
      center: safeCenter(liveCenter || nivel3Anchor),
      zoom: liveZoom,
      localidad,
      productorInfo,
      parcelasUnidad,
      totalAreaUnidad,
    });
  };

  // Cambia el riesgo de una comunidad (lo usan el nivel 1 y el nivel 2). Devuelve
  // la promesa para que el llamador pueda saber cuándo terminó de guardar.
  const updateRiesgoComunidad = (localidad, value) => {
    if (!(value > 0 || value === null)) return Promise.resolve();
    return http
      .put('/localidades_riesgos_ciclos', {
        id_localidad: localidad.id_localidad,
        id_ciclo: localidad.id_ciclo,
        id_cat_localidad_riesgo: value,
      })
      .then(() => fetchRiesgosLocalidades())
      .catch(e => {
        console.log(e);
        // Se vuelve a leer para que el selector regrese al valor guardado.
        fetchRiesgosLocalidades();
        Swal.fire({ icon: 'error', title: '', text: 'No se pudo actualizar el riesgo de la comunidad.' });
      });
  };

  const handleChange = (event, localidad) => {
    updateRiesgoComunidad(localidad, event.target.value);
  };

  // Nivel 2: igual que handleChange pero bloquea el selector mientras guarda.
  const handleChangeRiesgoNivel2 = event => {
    if (!localidad || savingRiesgo) return;
    setSavingRiesgo(true);
    updateRiesgoComunidad(localidad, event.target.value).finally(() => setSavingRiesgo(false));
  };

  // ---------------- Nivel 1: columnas + acciones ----------------

  const goToLocalidad = data => {
    goToMapasUrl({ idLocalidad: data.id_localidad });
  };

  const centerOnLocalidad = data => {
    if (!isValidLat(data.localidad_latitud) || !isValidLng(data.localidad_longitud)) return;
    const pos = { lat: Number(data.localidad_latitud), lng: Number(data.localidad_longitud) };
    if (mapLocalidades) {
      mapLocalidades.panTo(pos);
    } else {
      setCenterLocalidades(pos);
    }
  };

  const handleMarkerClickLocalidad = data => {
    setSearchNivel1('');
    setHighlightedLocalidadId(data.id_localidad);
  };

  useEffect(() => {
    if (highlightedLocalidadId == null) return;
    const timer = setTimeout(() => setHighlightedLocalidadId(null), 1800);
    return () => clearTimeout(timer);
  }, [highlightedLocalidadId]);

  const resetToNivel1 = () => {
    goToMapasUrl({});
  };

  const resetToNivel2 = () => {
    goToMapasUrl({ idLocalidad: localidad?.id_localidad });
  };

  const dataLocalidadesNumeradas = useMemo(
    () =>
      dataLocalidades.map((loc, i) => {
        const riesgo = riesgosPorLocalidad[loc.id_localidad];
        const idRiesgo = riesgo ? riesgo.id_cat_localidad_riesgo : loc.id_cat_localidad_riesgo;
        return {
          ...loc,
          numero: i + 1,
          id_cat_localidad_riesgo: idRiesgo,
          cat_localidad_riesgo: riesgo ? riesgo.cat_localidad_riesgo : loc.cat_localidad_riesgo,
          cat_localidad_riesgo_color: getRiesgoColor(idRiesgo),
        };
      }),
    [dataLocalidades, riesgosPorLocalidad]
  );

  const columnsLocalidades = React.useMemo(
    () => [
      {
        Header: 'CLAVE',
        accessor: 'numero',
        sortable: true,
        width: 62,
        minWidth: 62,
        Cell: ({ row, value }) => {
          const canCenter = isValidLat(row.original.localidad_latitud) && isValidLng(row.original.localidad_longitud);
          const sinUbicacion = 'Esta comunidad no tiene ubicación registrada, por eso no aparece en el mapa.';
          return (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              {canCenter ? (
                <i
                  className="fa fa-crosshairs"
                  onClick={() => centerOnLocalidad(row.original)}
                  title="Centrar en el mapa"
                  aria-hidden="true"
                  style={{ color: '#789D16', cursor: 'pointer', fontSize: 12 }}
                />
              ) : (
                <i
                  className="fa fa-exclamation-triangle"
                  title={sinUbicacion}
                  aria-label={sinUbicacion}
                  style={{ color: env.colors.secondary_300, cursor: 'help', fontSize: 12 }}
                />
              )}
              <span>{value}</span>
            </div>
          );
        },
      },
      {
        Header: 'COMUNIDAD',
        accessor: 'localidad',
        sortable: true,
        minWidth: 50,
        Cell: ({ row, value }) => (
          <a onClick={() => goToLocalidad(row.original)} className="m-r-2" style={{ color: '#789D16', cursor: 'pointer' }}>
            <span>{value}</span>
          </a>
        ),
      },
      { Header: 'MUNICIPIO', accessor: 'municipio', sortable: true, minWidth: 50 },
      {
        Header: 'RIESGO',
        accessor: 'cat_localidad_riesgo',
        sortable: true,
        width: 85,
        Cell: ({ row }) => (
          <SelectRiesgo
            value={row.original.id_cat_localidad_riesgo}
            color={row.original.cat_localidad_riesgo_color}
            onChange={event => handleChange(event, row.original)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapLocalidades]
  );

  const dataLocalidadesFiltradas = useMemo(() => {
    const term = searchNivel1.trim().toLowerCase();
    if (!term) return dataLocalidadesNumeradas;
    return dataLocalidadesNumeradas.filter(l => (l.localidad || '').toLowerCase().includes(term) || String(l.numero).includes(term));
  }, [dataLocalidadesNumeradas, searchNivel1]);

  useEffect(() => {
    if (highlightedLocalidadId == null) return;
    if (mapSideTableNivel1Ref.current) {
      mapSideTableNivel1Ref.current.scrollToRow(row => row.id_localidad === highlightedLocalidadId);
    }
  }, [highlightedLocalidadId, dataLocalidadesFiltradas]);

  const riesgoCounts = useMemo(() => {
    const counts = {};
    dataLocalidadesNumeradas.forEach(l => {
      const key = l.id_cat_localidad_riesgo ?? 'sinAsignar';
      counts[key] = (counts[key] || 0) + 1;
    });
    return [
      { label: 'Riesgo bajo', color: getRiesgoColor(1), count: counts[1] || 0 },
      { label: 'Riesgo medio', color: getRiesgoColor(2), count: counts[2] || 0 },
      { label: 'Riesgo alto', color: getRiesgoColor(3), count: counts[3] || 0 },
      { label: 'Sin asignar', color: getRiesgoColor(null), count: counts.sinAsignar || 0 },
    ];
  }, [dataLocalidadesNumeradas]);

  // ---------------- Nivel 2: agrupar por productor ----------------

  const productoresAgrupados = useMemo(() => {
    const map = new Map();
    dataProductoresLocalidad.forEach(p => {
      if (!p.productor_uuid) return;
      if (!map.has(p.productor_uuid)) {
        const riesgo = getRiesgoProductor(p);
        map.set(p.productor_uuid, {
          productor_uuid: p.productor_uuid,
          productor_id: p.productor_id,
          productor_nombre: p.productor_nombre,
          cuenta_categoria: p.cuenta_categoria,
          cuenta_categoria_color: p.cuenta_categoria_color,
          // El riesgo es del productor: se toma de su primera parcela.
          nivel_riesgo_id: riesgo.id,
          nivel_riesgo: riesgo.label,
          nivel_riesgo_color: riesgo.color,
          nivel_riesgo_orden: riesgo.orden,
          parcelas: [],
        });
      }
      // El productor se lista aunque ninguna parcela tenga ubicación; solo se cuentan las parcelas que aparecen en el mapa.
      if (getParcelaPosition(p)) map.get(p.productor_uuid).parcelas.push(p);
    });
    return Array.from(map.values()).map(g => ({ ...g, num_parcelas: g.parcelas.length }));
  }, [dataProductoresLocalidad]);

  const productoresFiltrados = useMemo(() => {
    const term = searchNivel2.trim().toLowerCase();
    if (!term) return productoresAgrupados;
    return productoresAgrupados.filter(
      p => (p.productor_nombre || '').toLowerCase().includes(term) || String(p.productor_id).includes(term)
    );
  }, [productoresAgrupados, searchNivel2]);

  const categoriaCounts = useMemo(() => {
    const groups = {};
    productoresAgrupados.forEach(p => {
      const key = p.cuenta_categoria || 'Sin categoría';
      if (!groups[key]) groups[key] = { label: key, color: p.cuenta_categoria_color, count: 0 };
      groups[key].count++;
    });
    return Object.values(groups);
  }, [productoresAgrupados]);

  // Riesgo vigente de la comunidad: viene de riesgosPorLocalidad (se refresca al
  // guardar), no del objeto `localidad`, que queda con el valor de cuando se abrió.
  const riesgoComunidad = useMemo(() => {
    if (!localidad) return { id: null, color: getRiesgoColor(null) };
    const riesgo = riesgosPorLocalidad[localidad.id_localidad];
    const id = riesgo ? riesgo.id_cat_localidad_riesgo : localidad.id_cat_localidad_riesgo;
    return { id, color: getRiesgoColor(id) };
  }, [localidad, riesgosPorLocalidad]);

  // Resumen "2 Alto · 4 Medio · 8 Sin Riesgo" de los productores, de mayor a menor severidad.
  const riesgoProductoresResumen = useMemo(() => {
    const groups = {};
    productoresAgrupados.forEach(p => {
      const key = p.nivel_riesgo_orden;
      if (!groups[key]) groups[key] = { orden: key, label: p.nivel_riesgo || 'Sin asignar', count: 0 };
      groups[key].count++;
    });
    return Object.values(groups)
      .sort((a, b) => a.orden - b.orden)
      .map(g => `${g.count} ${g.label}`)
      .join(' · ');
  }, [productoresAgrupados]);

  const justClickedProductorRef = useRef(false);
  const handleSelectProductor = productor => {
    if (productor.num_parcelas === 1) {
      goToParcelaDetalle(productor.parcelas[0]);
      return;
    }
    justClickedProductorRef.current = true;
    goToMapasUrl({ idLocalidad: localidad?.id_localidad, productorUuid: productor.productor_uuid });
  };

  useEffect(() => {
    if (!urlIdLocalidad) {
      if (localidad !== null) {
        setLocalidad(null);
        setProductorSeleccionado(null);
      }
      return;
    }
    if (String(localidad?.id_localidad) === String(urlIdLocalidad)) return;
    if (blockingLocalidades) return;
    const data = dataLocalidadesNumeradas.find(l => String(l.id_localidad) === String(urlIdLocalidad));
    if (!data) return;

    setLocalidad(data);
    setProductorSeleccionado(null);
    setSearchNivel2('');
    if (incomingMapStateRef.current && !urlProductorUuid) {
      setCenterLocalidades(incomingMapStateRef.current.center);
      setCenterProductores(incomingMapStateRef.current.center);
      setZoomLocalidades(incomingMapStateRef.current.zoom);
      setZoomProductores(incomingMapStateRef.current.zoom);
      incomingMapStateRef.current = null;
    } else if (isValidLat(data.localidad_latitud) && isValidLng(data.localidad_longitud)) {
      const center = { lat: Number(data.localidad_latitud), lng: Number(data.localidad_longitud) };
      setCenterLocalidades(center);
      setCenterProductores(center);
      setZoomLocalidades(14);
      setZoomProductores(14);
    }

    setBlockingProductores(true);
    setGeoPuntosLocalidad([]);

    Api.search('tx_padron_mapas_detalle', {
      filter: [
        { column: 'id_localidad', operator: '=', value: data.id_localidad },
        { column: 'id_ciclo', operator: '=', value: data.id_ciclo },
      ],
    })
      .then(response => {
        setDataProductoresLocalidad(response.data);
        setBlockingProductores(false);
      })
      .catch(() => setBlockingProductores(false));

    if (data.localidad_uuid) {
      Api.get('localidades_simbologia', data.localidad_uuid)
        .then(response => setGeoPuntosLocalidad(response.data || []))
        .catch(() => setGeoPuntosLocalidad([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlIdLocalidad, dataLocalidadesNumeradas, blockingLocalidades, localidad]);

  useEffect(() => {
    if (!urlProductorUuid) {
      if (productorSeleccionado !== null) setProductorSeleccionado(null);
      return;
    }
    if (productorSeleccionado === urlProductorUuid) return;
    if (blockingProductores) return;
    const productor = productoresAgrupados.find(p => p.productor_uuid === urlProductorUuid);
    if (!productor) return;

    const skipMapAnimation = !justClickedProductorRef.current;
    justClickedProductorRef.current = false;

    setMapUnidad(null);
    setMapsUnidad(null);
    setMapReadyUnidad(false);
    setProductorSeleccionado(productor.productor_uuid);
    if (!skipMapAnimation) fitBoundsToParcelas(mapProductores, mapsProductores, productor.parcelas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProductorUuid, productoresAgrupados, blockingProductores, productorSeleccionado]);

  const centerOnProductor = productor => {
    fitBoundsToParcelas(mapProductores, mapsProductores, productor.parcelas);
  };

  const handleMarkerClickProductor = productor => {
    setSearchNivel2('');
    setHighlightedProductorUuid(productor.productor_uuid);
  };

  useEffect(() => {
    if (highlightedProductorUuid == null) return;
    const timer = setTimeout(() => setHighlightedProductorUuid(null), 1800);
    return () => clearTimeout(timer);
  }, [highlightedProductorUuid]);

  useEffect(() => {
    if (highlightedProductorUuid == null) return;
    if (mapSideTableNivel2Ref.current) {
      mapSideTableNivel2Ref.current.scrollToRow(row => row.productor_uuid === highlightedProductorUuid);
    }
  }, [highlightedProductorUuid, productoresFiltrados]);

  const columnsProductores = React.useMemo(
    () => [
      {
        Header: 'CÓDIGO/​CLAVE',
        accessor: 'productor_id',
        sortable: true,
        width: 76,
        minWidth: 76,
        Cell: ({ row, value }) => {
          const canCenter = row.original.parcelas?.some(p => getParcelaPosition(p));
          const sinUbicacion = 'Ninguna parcela de este productor tiene ubicación registrada, por eso no aparece en el mapa.';
          return (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              {canCenter ? (
                <i
                  className="fa fa-crosshairs"
                  onClick={() => centerOnProductor(row.original)}
                  title="Centrar en el mapa"
                  aria-hidden="true"
                  style={{ color: '#789D16', cursor: 'pointer', fontSize: 12 }}
                />
              ) : (
                <i
                  className="fa fa-exclamation-triangle"
                  title={sinUbicacion}
                  aria-label={sinUbicacion}
                  style={{ color: env.colors.secondary_300, cursor: 'help', fontSize: 12 }}
                />
              )}
              <span>{value}</span>
            </div>
          );
        },
      },
      {
        Header: 'PRODUCTOR',
        accessor: 'productor_nombre',
        sortable: true,
        minWidth: 60,
        Cell: ({ row, value }) => (
          <a onClick={() => handleSelectProductor(row.original)} className="m-r-2" style={{ color: '#789D16', cursor: 'pointer' }}>
            <span>{value}</span>
          </a>
        ),
      },
      { Header: '# DE PARCELAS', accessor: 'num_parcelas', sortable: true, width: 66 },
      {
        Header: 'CATEGORÍA',
        accessor: 'cuenta_categoria',
        sortable: true,
        minWidth: 80,
        Cell: ({ row, value }) => <LabelCategory label={value} color={row.original.cuenta_categoria_color}></LabelCategory>,
      },
      {
        Header: 'RIESGO',
        // Se ordena por severidad (Alto primero), no alfabéticamente por la etiqueta.
        accessor: 'nivel_riesgo_orden',
        sortable: true,
        minWidth: 66,
        Cell: ({ row }) => (
          <LabelCategory label={row.original.nivel_riesgo || 'Sin asignar'} color={row.original.nivel_riesgo_color}></LabelCategory>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapProductores, mapsProductores]
  );

  // ---------------- Nivel 3: unidad de producción ----------------

  const productorInfo = useMemo(() => productoresAgrupados.find(p => p.productor_uuid === productorSeleccionado) || null, [
    productoresAgrupados,
    productorSeleccionado,
  ]);

  const parcelasUnidad = useMemo(
    () => dataProductoresLocalidad.filter(p => p.productor_uuid === productorSeleccionado && getParcelaPosition(p)),
    [dataProductoresLocalidad, productorSeleccionado]
  );

  const totalAreaUnidad = useMemo(
    () => parcelasUnidad.reduce((acc, p) => (!isNaN(parseFloat(p.parcela_superficie)) ? acc + parseFloat(p.parcela_superficie) : acc), 0),
    [parcelasUnidad]
  );

  const categoriaCountsUnidad = useMemo(() => {
    if (!productorInfo) return [];
    return [
      {
        label: productorInfo.cuenta_categoria || 'Sin asignar',
        color: productorInfo.cuenta_categoria_color,
        count: parcelasUnidad.length,
      },
    ];
  }, [productorInfo, parcelasUnidad]);

  const goToParcelaUnidad = data => {
    const pos = getParcelaCentro(data);
    if (pos) {
      setCenterUnidad(pos);
      setZoomUnidad(18);
    }
  };

  const handleMarkerClickParcelaUnidad = data => {
    setHighlightedParcelaId(data.id_parcela);
  };

  useEffect(() => {
    if (highlightedParcelaId == null) return;
    const timer = setTimeout(() => setHighlightedParcelaId(null), 1800);
    return () => clearTimeout(timer);
  }, [highlightedParcelaId]);

  useEffect(() => {
    if (highlightedParcelaId == null) return;
    if (mapSideTableNivel3Ref.current) {
      mapSideTableNivel3Ref.current.scrollToRow(row => row.id_parcela === highlightedParcelaId);
    }
  }, [highlightedParcelaId, parcelasUnidad]);

  const goToParcelaDetalle = data => {
    history.push(`/admin/parcelas/perfil/${data.parcela_uuid}`);
  };

  const columnsParcelasUnidad = React.useMemo(
    () => [
      {
        Header: 'PARCELA',
        accessor: 'id_parcela',
        sortable: true,
        width: 100,
        minWidth: 100,
        Cell: ({ row, value }) => {
          const canCenter = !!getParcelaPosition(row.original);
          return (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              <i
                className="fa fa-crosshairs"
                onClick={canCenter ? () => goToParcelaUnidad(row.original) : undefined}
                title={canCenter ? 'Centrar en el mapa' : 'Sin ubicación disponible para centrar'}
                aria-hidden="true"
                style={{
                  color: canCenter ? '#789D16' : '#C4C4C4',
                  cursor: canCenter ? 'pointer' : 'default',
                  fontSize: 12,
                }}
              />
              <span>{value}</span>
            </div>
          );
        },
      },
      {
        Header: `NOMBRE DE ${transformWord(etiquetas?.parcela, ['singular', 'uppercase'])}`,
        accessor: 'parcela_nombre',
        sortable: true,
        minWidth: 90,
        Cell: ({ row, value }) => (
          <a onClick={() => goToParcelaDetalle(row.original)} className="m-r-2" style={{ color: '#789D16', cursor: 'pointer' }}>
            <span>{value}</span>
          </a>
        ),
      },
      {
        Header: 'ÁREA',
        accessor: 'parcela_superficie',
        sortable: true,
        width: 72,
        Cell: ({ value }) =>
          value != null && !isNaN(parseFloat(value))
            ? `${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`
            : '—',
      },
    ],
    [etiquetas]
  );

  useEffect(() => {
    if (!productorSeleccionado) {
      setGeoPuntosUnidad([]);
      return;
    }
    if (mapUnidad && mapsUnidad) {
      if (incomingMapStateRef.current) {
        setCenterUnidad(incomingMapStateRef.current.center);
        setZoomUnidad(incomingMapStateRef.current.zoom);
        incomingMapStateRef.current = null;
      } else {
        fitBoundsToParcelas(mapUnidad, mapsUnidad, parcelasUnidad);
      }
      setMapReadyUnidad(true);
    }
    Api.get('cuentas_simbologia', productorSeleccionado)
      .then(response => setGeoPuntosUnidad(response.data || []))
      .catch(() => setGeoPuntosUnidad([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productorSeleccionado, mapUnidad, mapsUnidad]);

  useEffect(() => {
    if (mapUnidad && mapsUnidad) {
      let p = parcelasUnidad
        .filter(parcela => parcela.poligono !== null)
        .map(parcela => {
          let poligono = JSON.parse(parcela.poligono);
          let poligono_data = poligono.coordinates[0].map(pt => ({ lat: pt[1], lng: pt[0] }));
          var polygon = new mapsUnidad.Polygon({
            paths: poligono_data,
            strokeColor: parcela.cuenta_categoria_color || '#91B833',
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: parcela.cuenta_categoria_color || '#91B833',
            fillOpacity: 0.25,
          });
          polygon.setMap(mapUnidad);
          return polygon;
        });
      setPoligonosUnidad(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapUnidad, mapsUnidad, parcelasUnidad]);

  useEffect(() => {
    poligonosUnidadOld.forEach(polygon => polygon.setMap(null));
    setPoligonosUnidadOld(poligonosUnidad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poligonosUnidad]);

  const renderMapProductores = (map, maps) => {
    setMapProductores(map);
    setMapsProductores(maps);
  };

  const renderMapUnidad = (map, maps) => {
    setMapUnidad(map);
    setMapsUnidad(maps);
  };

  const getDataLocalidades = () => {
    setBlockingLocalidades(true);
    setLocalidad(null);
    setProductorSeleccionado(null);

    Api.search('tx_padron_mapas', { filter: [{ column: 'id_ciclo', operator: '=', value: idCiclo }] })
      .then(response => {
        setDataLocalidades(response.data);
        setBlockingLocalidades(false);
        if (incomingMapStateRef.current && !urlIdLocalidad) {
          setCenterLocalidades(incomingMapStateRef.current.center);
          setZoomLocalidades(incomingMapStateRef.current.zoom);
          incomingMapStateRef.current = null;
          return;
        }
        const conCoordenadas = response.data.find(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud));
        if (conCoordenadas) {
          setCenterLocalidades({
            lat: Number(conCoordenadas.localidad_latitud),
            lng: Number(conCoordenadas.localidad_longitud),
          });
        }
      })
      .catch(() => setBlockingLocalidades(false));
  };

  const fetchRiesgosLocalidades = () => {
    Api.getAll('localidades_riesgos_ciclos')
      .then(response => {
        const lista = response.data?.localidades || [];
        const mapa = {};
        lista.forEach(l => {
          mapa[l.id_localidad] = {
            id_cat_localidad_riesgo: l.id_cat_localidad_riesgo,
            cat_localidad_riesgo: l.riesgo_nombre,
            cat_localidad_riesgo_color: l.riesgo_color,
          };
        });
        setRiesgosPorLocalidad(mapa);
      })
      .catch(() => setRiesgosPorLocalidad({}));
  };

  useEffect(() => {
    if (idCiclo > 0) {
      getDataLocalidades();
      fetchRiesgosLocalidades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCiclo]);

  useEffect(() => {
    if (idCiclo <= 0) return;
    Api.search('tx_padron_mapas_detalle', { filter: [{ column: 'id_ciclo', operator: '=', value: idCiclo }] })
      .then(response => setDataProductores(response.data))
      .catch(() => {});
  }, [idCiclo]);

  useEffect(() => {
    Api.getAll('cooperativas_simbologia')
      .then(response => setGeoPuntosCooperativa(response.data || []))
      .catch(() => setGeoPuntosCooperativa([]));
  }, []);

  const renderMapLocalidades = (map, maps) => {
    setMapLocalidades(map);
    setMapsLocalidades(maps);
  };

  const onChangeMap = event => setZoomProductores(event.zoom);

  const recenterLocalidades = () =>
    fitMapToPositions(
      mapLocalidades,
      mapsLocalidades,
      dataLocalidades
        .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
        .map(loc => ({ lat: Number(loc.localidad_latitud), lng: Number(loc.localidad_longitud) }))
    );

  const recenterProductores = () => {
    const localidadPos =
      isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud)
        ? { lat: Number(localidad.localidad_latitud), lng: Number(localidad.localidad_longitud) }
        : null;
    fitMapToPositions(mapProductores, mapsProductores, [localidadPos, ...dataProductoresLocalidad.map(p => getParcelaPosition(p))]);
  };

  const recenterUnidad = () =>
    fitMapToPositions(
      mapUnidad,
      mapsUnidad,
      parcelasUnidad.flatMap(p => getParcelaVertices(p))
    );

  return (
    <div>
      <div className="row justify-content-between align-items-center mb-2 no-print" style={{ gap: 10, marginLeft: 1 }}>
        <SelectCiclo setFilter={evt => setIdCiclo(evt.target.id)}></SelectCiclo>
        {/* {dataProductores.length > 0 && (
          <a
            onClick={() => descargaKML(dataProductores, transformWord(etiquetas?.parcela, ['plural', 'lowercase']))}
            style={{ color: '#789D16', cursor: 'pointer' }}>
            <img src={env.images.icon.kml} style={{ marginRight: 6 }} />
            Descargar Mapa KML
          </a>
        )} */}
      </div>

      {/* ---------------- Nivel 1: Comunidades ---------------- */}
      {localidad === null && (
        <div>
          <div className="row justify-content-between align-items-start mb-2">
            <div>
              <h1 className="page-header mb-0 ml-2">MAPA DE COMUNIDADES</h1>
            </div>
            <div className="no-print mr-2">
              <button
                type="button"
                onClick={goToPrintNivel1}
                className="btn btn-sirio"
                style={{ borderRadius: 20 }}
                disabled={blockingLocalidades}>
                <i className="fa fa-download mr-2" aria-hidden="true"></i> Exportar PDF
              </button>
            </div>
          </div>

          <Breadcrumb items={[{ label: 'PADRÓN' }, { label: 'COMUNIDADES', active: true }]} />

          <div className="row pb-4">
            <div className="col-lg-8 mb-3 mb-lg-0">
              <CardContainer
                height={topHeight + 254}
                style={{
                  paddingTop: 16,
                  paddingBottom: 16,
                }}>
                <Panel>
                  <div className="row mb-2 align-items-center">
                    <div className="col-6">
                      <span style={{ fontSize: 16, fontWeight: 600, color: env.colors.neutral_900 }} className="font-lato-normal-600">
                        Vista general · {dataLocalidades.length} comunidades
                      </span>
                    </div>
                    <div className="col-6 d-flex justify-content-end">
                      <MapTypeToggle value={mapTypeNivel1} onChange={setMapTypeNivel1} />
                    </div>
                  </div>

                  <BlockUi tag="div" message="" blocking={blockingLocalidades} renderChildren={true}>
                    <div
                      id="GoogleMapReact"
                      className="map-full-bleed"
                      style={{ height: '650px', margin: '0 -30px', position: 'relative' }}>
                      <NorthIcon color={mapTypeNivel1 === 'satellite' || mapTypeNivel1 === 'hybrid' ? 'white' : 'black'} />
                      {dataLocalidades.length !== 0 && <MapRecenterButton onClick={recenterLocalidades} />}
                      {dataLocalidades.length !== 0 && (
                        <GoogleMapReact
                          bootstrapURLKeys={{ key: env.googleApiKey }}
                          options={() => ({ mapTypeId: mapTypeNivel1, scaleControl: true })}
                          center={safeCenter(centerLocalidades)}
                          zoom={zoomLocalidades}
                          yesIWantToUseGoogleMapApiInternals
                          onGoogleApiLoaded={({ map, maps }) => renderMapLocalidades(map, maps)}>
                          {dataLocalidadesNumeradas
                            .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
                            .map((loc, i) => (
                              <CircleMarker
                                key={i}
                                lat={loc.localidad_latitud}
                                lng={loc.localidad_longitud}
                                number={loc.numero}
                                color={loc.cat_localidad_riesgo_color}
                                title={`${loc.localidad} · ${loc.cat_localidad_riesgo || 'Sin asignar'}`}
                                onClick={() => handleMarkerClickLocalidad(loc)}
                              />
                            ))}
                        </GoogleMapReact>
                      )}
                      {dataLocalidades.length === 0 && (
                        <CardBasic
                          height={topHeight - 60}
                          label="Ubicaciónes no disponibles"
                          value={':('}
                          style={{ backgroundColor: env.colors.neutral_1 }}></CardBasic>
                      )}
                    </div>
                    <MapLegendRow items={riesgoCounts} />
                  </BlockUi>
                </Panel>
              </CardContainer>
            </div>

            <div className="col-lg-4">
              <CardContainer
                height={topHeight + 254}
                style={{
                  paddingTop: 20,
                  paddingBottom: 0,
                }}>
                <MapSideTable
                  ref={mapSideTableNivel1Ref}
                  className="map-side-table-comunidades"
                  title="Comunidades"
                  count={dataLocalidadesFiltradas.length}
                  blocking={blockingLocalidades}
                  columns={columnsLocalidades}
                  data={dataLocalidadesFiltradas}
                  searchValue={searchNivel1}
                  onSearchChange={e => setSearchNivel1(e.target.value)}
                  searchPlaceholder="Buscar comunidad, clave"
                  getRowProps={row => ({
                    style: {
                      backgroundColor: row.id_localidad === highlightedLocalidadId ? '#E9EFDA' : 'transparent',
                      borderRadius: 6,
                      transition: 'background-color 400ms ease-out',
                    },
                  })}
                />
              </CardContainer>
            </div>
          </div>

          <div className="row pb-4">
            <div className="col-12">
              <SimbologiaLegend
                legendData={legendDataCooperativa}
                panTo={pos => mapLocalidades && mapLocalidades.panTo(pos)}
                coordsCenter={coordsCenter}
                hasData
                showToggleButton={geoPuntosCooperativa.length > 0}
                show={showSimbologiaNivel1}
                onToggleShow={() => setShowSimbologiaNivel1(v => !v)}
                showEditButton
                onEditClick={() => {
                  const liveCenter = mapLocalidades
                    ? { lat: mapLocalidades.getCenter().lat(), lng: mapLocalidades.getCenter().lng() }
                    : null;
                  const liveZoom = mapLocalidades ? mapLocalidades.getZoom() : zoomLocalidades;
                  history.push('/admin/padron/mapas/editsimbologia/cooperativa', {
                    center: safeCenter(liveCenter || centerLocalidades),
                    zoom: liveZoom,
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Nivel 2: Parcelas/productores de la comunidad ---------------- */}
      {localidad !== null && productorInfo === null && (
        <div>
          <div className="row justify-content-between align-items-start mb-2">
            <div>
              <h1 className="page-header mb-0 ml-2">MAPA DE {localidad.localidad?.toUpperCase()}</h1>
            </div>
            <div className="no-print mr-2">
              <button
                type="button"
                onClick={goToPrintNivel2}
                className="btn btn-sirio"
                style={{ borderRadius: 20 }}
                disabled={blockingProductores}>
                <i className="fa fa-download mr-2" aria-hidden="true"></i> Exportar PDF
              </button>
            </div>
          </div>

          <Breadcrumb
            items={[{ label: 'PADRÓN' }, { label: 'COMUNIDADES', onClick: resetToNivel1 }, { label: localidad.localidad, active: true }]}
          />

          <div className="row pb-4">
            <div className="col-lg-7 mb-3 mb-lg-0">
              <CardContainer height={topHeight + 254} style={{ paddingTop: 16, paddingBottom: 16 }}>
                <Panel>
                  <div className="row mb-2 align-items-center">
                    <div className="col-6">
                      <span style={{ fontSize: 16, fontWeight: 600, color: env.colors.neutral_900 }} className="font-lato-normal-600">
                        Parcelas y Productores de la Comunidad
                      </span>
                    </div>
                    <div className="col-6 d-flex justify-content-end">
                      <MapTypeToggle value={mapTypeNivel2} onChange={setMapTypeNivel2} />
                    </div>
                  </div>

                  <BlockUi tag="div" message="" blocking={blockingProductores} renderChildren={true}>
                    <div className="map-full-bleed" style={{ height: '650px', margin: '0 -30px', position: 'relative' }}>
                      <NorthIcon color={mapTypeNivel2 === 'satellite' || mapTypeNivel2 === 'hybrid' ? 'white' : 'black'} />
                      <MapRecenterButton onClick={recenterProductores} />
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: env.googleApiKey }}
                        options={() => ({ mapTypeId: mapTypeNivel2, scaleControl: true })}
                        center={safeCenter(centerProductores)}
                        zoom={zoomProductores}
                        onChange={event => onChangeMap(event)}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={({ map, maps }) => renderMapProductores(map, maps)}>
                        {isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud) && (
                          <CircleMarker
                            lat={localidad?.localidad_latitud}
                            lng={localidad?.localidad_longitud}
                            number=""
                            color="#000"
                            size={14}
                            title={localidad?.localidad}
                          />
                        )}

                        {dataProductoresLocalidad
                          .map(productor => ({ ...productor, _pos: getParcelaPosition(productor) }))
                          .filter(productor => productor._pos)
                          .map((productor, i) => (
                            <PillMarker
                              key={i}
                              lat={productor._pos.lat}
                              lng={productor._pos.lng}
                              label={productor.productor_id}
                              color={productor.cuenta_categoria_color}
                              title={`${productor.productor_nombre} · ${productor.parcela_nombre}`}
                              onClick={() => handleMarkerClickProductor(productor)}
                            />
                          ))}
                      </GoogleMapReact>
                    </div>
                    <MapLegendRow items={categoriaCounts} />
                  </BlockUi>
                </Panel>
              </CardContainer>
            </div>

            <div className="col-lg-5">
              <CardContainer
                height={topHeight + 254}
                style={{
                  paddingTop: 20,
                  paddingBottom: 0,
                }}>
                <MapSideTable
                  ref={mapSideTableNivel2Ref}
                  className="map-side-table-productores"
                  title="Productores"
                  count={productoresFiltrados.length}
                  blocking={blockingProductores}
                  columns={columnsProductores}
                  data={productoresFiltrados}
                  searchValue={searchNivel2}
                  onSearchChange={e => setSearchNivel2(e.target.value)}
                  searchPlaceholder="Buscar productor, clave"
                  headerExtra={
                    localidad && (
                      <>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: env.colors.neutral_900 }}>Riesgo de la comunidad</span>
                          <SelectRiesgo
                            value={riesgoComunidad.id}
                            color={riesgoComunidad.color}
                            onChange={handleChangeRiesgoNivel2}
                            disabled={savingRiesgo}
                          />
                        </div>
                        {riesgoProductoresResumen && (
                          <div style={{ fontSize: 11, color: env.colors.neutral_400, marginTop: 6 }}>{riesgoProductoresResumen}</div>
                        )}
                      </>
                    )
                  }
                  getRowProps={row => ({
                    style: {
                      backgroundColor: row.productor_uuid === highlightedProductorUuid ? '#E9EFDA' : 'transparent',
                      borderRadius: 6,
                      transition: 'background-color 400ms ease-out',
                    },
                  })}
                />
              </CardContainer>
            </div>
          </div>

          <div className="row pb-4">
            <div className="col-12">
              <SimbologiaLegend
                legendData={legendDataLocalidad}
                panTo={pos => mapProductores && mapProductores.panTo(pos)}
                coordsCenter={coordsCenter}
                hasData={
                  !!localidad?.localidad_uuid ||
                  geoPuntosLocalidad.length > 0 ||
                  (isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud))
                }
                extraPoint={
                  isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud)
                    ? {
                        label: localidad?.localidad || 'Comunidad',
                        color: '#000',
                        onClick: () =>
                          mapProductores &&
                          mapProductores.panTo({ lat: Number(localidad.localidad_latitud), lng: Number(localidad.localidad_longitud) }),
                      }
                    : null
                }
                showToggleButton={geoPuntosLocalidad.length > 0}
                show={showSimbologiaNivel2}
                onToggleShow={() => setShowSimbologiaNivel2(v => !v)}
                showEditButton={!!localidad?.localidad_uuid}
                onEditClick={() => {
                  const liveCenter = mapProductores
                    ? { lat: mapProductores.getCenter().lat(), lng: mapProductores.getCenter().lng() }
                    : null;
                  const liveZoom = mapProductores ? mapProductores.getZoom() : zoomProductores;
                  history.push(`/admin/padron/mapas/editsimbologia/localidad/${localidad.localidad_uuid}`, {
                    center: liveCenter || nivel2Anchor,
                    zoom: liveZoom,
                    idLocalidad: localidad.id_localidad,
                    localidadNombre: localidad.localidad,
                    municipio: localidad.municipio,
                    localidadCentro: nivel2Anchor,
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Nivel 3: Unidad de producción ---------------- */}
      {productorInfo !== null && (
        <div>
          <div className="row justify-content-between align-items-start mb-2">
            <div>
              <h1 className="page-header mb-0 ml-2">Unidad de Producción - {productorInfo.productor_nombre}</h1>
            </div>
            <div className="no-print mr-2">
              <button
                type="button"
                onClick={goToPrintNivel3}
                className="btn btn-sirio"
                style={{ borderRadius: 20 }}
                disabled={!mapReadyUnidad}>
                <i className="fa fa-download mr-2" aria-hidden="true"></i> Exportar PDF
              </button>
            </div>
          </div>

          <Breadcrumb
            items={[
              { label: 'PADRÓN' },
              { label: 'COMUNIDADES', onClick: resetToNivel1 },
              { label: localidad.localidad, onClick: resetToNivel2 },
              { label: productorInfo.productor_nombre, active: true },
            ]}
          />

          <div className="row pb-4">
            <div className="col-lg-8 mb-3 mb-lg-0">
              <CardContainer height={topHeight + 254} style={{ paddingTop: 16, paddingBottom: 16 }}>
                <Panel>
                  <div className="row mb-2 align-items-center">
                    <div className="col-6">
                      <span style={{ fontSize: 16, fontWeight: 600, color: env.colors.neutral_900 }} className="font-lato-normal-600">
                        Parcelas del productor
                      </span>
                    </div>
                    <div className="col-6 d-flex justify-content-end">
                      <MapTypeToggle value={mapTypeNivel3} onChange={setMapTypeNivel3} />
                    </div>
                  </div>

                  <BlockUi tag="div" message="" blocking={!mapReadyUnidad} renderChildren={true}>
                    <div className="map-full-bleed" style={{ height: '650px', margin: '0 -30px', position: 'relative' }}>
                      <NorthIcon color={mapTypeNivel3 === 'satellite' || mapTypeNivel3 === 'hybrid' ? 'white' : 'black'} />
                      <MapRecenterButton onClick={recenterUnidad} />
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: env.googleApiKey, libraries: ['geometry', 'elevation'] }}
                        options={() => ({ mapTypeId: mapTypeNivel3, scaleControl: true })}
                        center={safeCenter(centerUnidad)}
                        zoom={zoomUnidad}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={({ map, maps }) => renderMapUnidad(map, maps)}>
                        {parcelasUnidad
                          .map(p => ({ ...p, _pos: getParcelaCentro(p) }))
                          .filter(p => p._pos)
                          .map((p, i) => (
                            <PillMarker
                              key={i}
                              lat={p._pos.lat}
                              lng={p._pos.lng}
                              label={p.parcela_nombre}
                              color={p.cuenta_categoria_color}
                              title={p.parcela_nombre}
                              onClick={() => handleMarkerClickParcelaUnidad(p)}
                            />
                          ))}
                      </GoogleMapReact>
                    </div>
                    <MapLegendRow items={categoriaCountsUnidad} />
                  </BlockUi>
                </Panel>
              </CardContainer>
            </div>

            <div className="col-lg-4">
              <CardContainer
                height={topHeight + 254}
                style={{
                  paddingTop: 20,
                  paddingBottom: 0,
                }}>
                <div className="map-side-table-parcelas-unidad-wrapper">
                  <MapSideTable
                    ref={mapSideTableNivel3Ref}
                    className="map-side-table-parcelas-unidad"
                    title={transformWord(etiquetas?.parcela, ['plural', 'capitalize'])}
                    count={parcelasUnidad.length}
                    blocking={false}
                    columns={columnsParcelasUnidad}
                    data={parcelasUnidad}
                    getRowProps={row => ({
                      style: {
                        backgroundColor: row.id_parcela === highlightedParcelaId ? '#E9EFDA' : 'transparent',
                        borderRadius: 6,
                        transition: 'background-color 400ms ease-out',
                      },
                    })}
                  />
                  <div className="d-flex justify-content-between" style={{ paddingBottom: 20, marginTop: -4 }}>
                    <span className="font-lato-normal-600" style={{ fontSize: 13 }}>
                      Total unidad de producción
                    </span>
                    <span className="font-weight-bold" style={{ fontSize: 13 }}>
                      {totalAreaUnidad.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha
                    </span>
                  </div>
                </div>
              </CardContainer>
            </div>
          </div>

          <div className="row pb-4">
            <div className="col-12">
              <SimbologiaLegend
                legendData={legendDataUnidad}
                panTo={pos => mapUnidad && mapUnidad.panTo(pos)}
                coordsCenter={coordsCenter}
                hasData={!!productorSeleccionado || geoPuntosUnidad.length > 0}
                showToggleButton={geoPuntosUnidad.length > 0}
                show={showSimbologiaNivel3}
                onToggleShow={() => setShowSimbologiaNivel3(v => !v)}
                showEditButton={!!productorSeleccionado}
                onEditClick={() => {
                  const liveCenter = mapUnidad ? { lat: mapUnidad.getCenter().lat(), lng: mapUnidad.getCenter().lng() } : null;
                  const liveZoom = mapUnidad ? mapUnidad.getZoom() : zoomUnidad;
                  history.push(`/admin/padron/mapas/editsimbologia/cuenta/${productorSeleccionado}`, {
                    center: liveCenter || nivel3Anchor,
                    zoom: liveZoom,
                    idLocalidad: localidad?.id_localidad,
                    productorNombre: productorInfo?.productor_nombre,
                    localidadNombre: localidad?.localidad,
                    municipio: localidad?.municipio,
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PadronMapasList;
